import Seo from "@/components/Seo";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Brain, Eye, Ear, MessageCircle, Heart, Hand, PersonStanding, Scissors,
  Wind, Activity, Utensils, Sparkles, Footprints, ExternalLink, ArrowRight,
  Star, Users, ShieldCheck, RefreshCw, Globe, Info, ChevronLeft, ChevronRight,
  Search, X, Share2, Check, Twitter, Linkedin, Link2,
} from "lucide-react";
import api from "@/lib/api";

const ICONS = {
  Brain, Eye, Ear, MessageCircle, Heart, Hand, PersonStanding, Scissors,
  Wind, Activity, Utensils, Sparkles, Footprints,
};

const PRICING = {
  Free: { c: "#22C55E", bg: "rgba(34,197,94,0.12)", b: "rgba(34,197,94,0.35)" },
  Freemium: { c: "#00F0FF", bg: "rgba(0,240,255,0.12)", b: "rgba(0,240,255,0.35)" },
  Paid: { c: "#F59E0B", bg: "rgba(245,158,11,0.12)", b: "rgba(245,158,11,0.35)" },
};

function PricingBadge({ pricing }) {
  const p = PRICING[pricing] || PRICING.Freemium;
  return (
    <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded"
      style={{ color: p.c, background: p.bg, border: `1px solid ${p.b}` }}>
      {pricing}
    </span>
  );
}

function PartLabel({ part, active, onClick, align, testId }) {
  const Icon = ICONS[part.icon] || Sparkles;
  return (
    <button
      data-testid={testId || `part-label-${part.id}`}
      onClick={onClick}
      className={`group flex items-center gap-3 w-full max-w-[220px] rounded-xl px-3 py-2 transition-all duration-300
        ${align === "right" ? "flex-row-reverse text-right" : "text-left"}
        ${active ? "bg-white/[0.06]" : "hover:bg-white/[0.04]"}`}
      style={active ? { boxShadow: `inset 0 0 0 1px ${part.color}55`, background: `${part.color}12` } : {}}
    >
      <span className="shrink-0 grid place-items-center w-11 h-11 rounded-xl transition-transform duration-300 group-hover:scale-110"
        style={{ color: part.color, background: `${part.color}14`, boxShadow: `inset 0 0 0 1px ${part.color}44` }}>
        <Icon className="w-5 h-5" />
      </span>
      <span className="min-w-0">
        <span className="block font-display font-bold text-[15px] leading-tight text-white">{part.name}</span>
        <span className="block text-[11px] text-[#94A3B8] truncate">{part.subtitle}</span>
      </span>
    </button>
  );
}

function Hotspot({ part, active, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`hotspot-${part.id}`}
      className="absolute -translate-x-1/2 -translate-y-1/2 group"
      style={{ left: `${part.pos.x}%`, top: `${part.pos.y}%` }}
      aria-label={part.name}
    >
      <span className="block rounded-full transition-all duration-300"
        style={{
          width: active ? 16 : 11, height: active ? 16 : 11,
          background: part.color,
          boxShadow: `0 0 0 4px ${part.color}22, 0 0 14px 2px ${part.color}`,
        }} />
      <span className="pointer-events-none absolute inset-0 rounded-full animate-ping"
        style={{ background: part.color, opacity: active ? 0.5 : 0.25 }} />
    </button>
  );
}

function ToolRow({ tool, index, color }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      data-testid={`tool-${index}`}
      className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-4 hover:bg-white/[0.05] hover:border-white/15 transition-all"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="grid place-items-center w-6 h-6 rounded-md text-[11px] font-bold shrink-0"
              style={{ color, background: `${color}18` }}>
              {tool.name.charAt(0)}
            </span>
            <h4 className="font-display font-bold text-white truncate">{tool.name}</h4>
          </div>
          <p className="text-sm text-[#94A3B8] mt-1.5 leading-snug">{tool.desc}</p>
        </div>
        <a href={tool.url} target="_blank" rel="noopener noreferrer"
          data-testid={`tool-link-${index}`}
          className="shrink-0 grid place-items-center w-8 h-8 rounded-lg text-[#94A3B8] hover:text-[#00F0FF] hover:bg-white/5 transition">
          <ExternalLink className="w-4 h-4" />
        </a>
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-3">
        <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-white/5 text-[#CBD5E1] border border-white/10">
          {tool.category}
        </span>
        <PricingBadge pricing={tool.pricing} />
        {tool.stat ? (
          <a href={tool.source} target="_blank" rel="noopener noreferrer"
            data-testid={`tool-stat-${index}`}
            className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded transition hover:brightness-125"
            style={{ color: "#5eead4", background: "rgba(20,184,166,0.12)", border: "1px solid rgba(20,184,166,0.35)" }}>
            {tool.stat} <ExternalLink className="w-2.5 h-2.5 opacity-70" />
          </a>
        ) : (
          <span className="text-[10px] font-medium px-2 py-0.5 rounded text-[#94A3B8] border border-white/10">
            {tool.market}
          </span>
        )}
      </div>
    </motion.div>
  );
}

function StatCard({ icon: Icon, value, label, color }) {
  return (
    <div className="card-surface rounded-2xl px-6 py-5 flex items-center gap-4 min-w-[190px]" data-testid="hero-stat">
      <span className="grid place-items-center w-12 h-12 rounded-xl" style={{ color, background: `${color}14` }}>
        <Icon className="w-6 h-6" />
      </span>
      <div>
        <div className="font-display font-extrabold text-2xl text-white leading-none">{value}</div>
        <div className="text-xs text-[#94A3B8] mt-1">{label}</div>
      </div>
    </div>
  );
}

const WHATS = [
  { icon: Star, title: "Curated", desc: "Handpicked top AI tools" },
  { icon: ShieldCheck, title: "Verified", desc: "Real usage data, sourced" },
  { icon: RefreshCw, title: "Updated", desc: "Regularly refreshed" },
  { icon: Globe, title: "Global", desc: "Used by people worldwide" },
];

export default function AIHuman() {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [copied, setCopied] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [views, setViews] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const carouselRef = useRef(null);
  const topRef = useRef(null);
  const searchBoxRef = useRef(null);
  const shareRef = useRef(null);

  useEffect(() => {
    api.get("/ai-human").then((r) => {
      setData(r.data);
      setViews(r.data.views || {});
      const paramId = searchParams.get("part");
      const valid = r.data.body_parts.find((p) => p.id === paramId);
      const def = valid || r.data.body_parts.find((p) => p.default) || r.data.body_parts[0];
      setSelected(def.id);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) setShowResults(false);
      if (shareRef.current && !shareRef.current.contains(e.target)) setShareOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const parts = data?.body_parts || [];
  const byId = useMemo(() => Object.fromEntries(parts.map((p) => [p.id, p])), [parts]);
  const active = byId[selected];
  const leftParts = parts.filter((p) => p.side === "left");
  const rightParts = parts.filter((p) => p.side === "right");
  const popularOrdered = useMemo(
    () => [...(data?.popular || [])].sort((a, b) => (data?.views?.[b] || 0) - (data?.views?.[a] || 0)),
    [data]
  );

  const results = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    const out = [];
    for (const p of parts) {
      if (p.name.toLowerCase().includes(q) || p.subtitle.toLowerCase().includes(q) || p.description.toLowerCase().includes(q)) {
        out.push({ partId: p.id, part: p, label: p.name, sub: p.subtitle, kind: "ability" });
      }
      for (const t of p.tools) {
        if (t.name.toLowerCase().includes(q) || t.category.toLowerCase().includes(q) || t.desc.toLowerCase().includes(q)) {
          out.push({ partId: p.id, part: p, label: t.name, sub: `${t.category} · in ${p.name}`, kind: "tool" });
        }
      }
    }
    return out.slice(0, 8);
  }, [search, parts]);

  const pick = (id) => {
    setSelected(id);
    setSearchParams({ part: id }, { replace: true });
    setViews((v) => ({ ...v, [id]: (v[id] || 0) + 1 }));
    api.post(`/ai-human/${id}/click`).catch(() => {});
    if (topRef.current && window.innerWidth < 1024) {
      topRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const chooseResult = (r) => {
    setSearch("");
    setShowResults(false);
    pick(r.partId);
  };

  const shareUrl = () => `${process.env.REACT_APP_BACKEND_URL}/api/share/ai-human?part=${active.id}`;

  const copyLink = () => {
    navigator.clipboard?.writeText(shareUrl()).then(() => {
      setCopied(true);
      toast.success("Link copied — share this AI ability!");
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => toast.error("Could not copy link"));
  };

  const shareOn = (network) => {
    const url = shareUrl();
    const text = `The best AI tools for ${active.name} (${active.subtitle}) — curated on WorldAIInfo`;
    const links = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    };
    window.open(links[network], "_blank", "noopener,noreferrer");
    setShareOpen(false);
  };

  if (!data) {
    return (
      <div className="min-h-[60vh] grid place-items-center text-[#94A3B8]" data-testid="ai-human-loading">
        <RefreshCw className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-10" data-testid="ai-human-page">
      <Seo title="AI Human — The Best AI Tools for Every Human Ability | WorldAIInfo" description="Explore the best AI tools mapped to human abilities — vision, voice, thinking, creativity and more — each with verified, sourced usage data." canonicalPath="/ai-human" />
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6" ref={topRef}>
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight">AI Human</h1>
            <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded bg-[#00F0FF]/15 text-[#00F0FF] border border-[#00F0FF]/30 mt-2">Beta</span>
          </div>
          <p className="text-[#94A3B8] mt-3 max-w-xl text-base">
            Discover the best AI tools for every human ability. Tap any body part to explore top tools —
            each with <span className="text-white">verified, sourced usage data</span> so you know what the world really uses.
          </p>
        </div>
        <div className="flex flex-wrap gap-4">
          <StatCard icon={Star} value={`${data.total_tools}+`} label="AI Tools Curated" color="#F59E0B" />
          <StatCard icon={Users} value={`${data.abilities}`} label="Human Abilities" color="#00F0FF" />
        </div>
      </div>

      {/* Search */}
      <div ref={searchBoxRef} className="relative mt-6 max-w-xl" data-testid="ai-human-search">
        <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 focus-within:border-[#00F0FF]/50 transition">
          <Search className="w-4 h-4 text-[#94A3B8] shrink-0" />
          <input
            data-testid="ai-human-search-input"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setShowResults(true); }}
            onFocus={() => setShowResults(true)}
            placeholder="Search an ability or tool — e.g. voice, images, coding, Midjourney…"
            className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-[#64748B]"
          />
          {search && (
            <button data-testid="ai-human-search-clear" onClick={() => { setSearch(""); setShowResults(false); }}
              className="text-[#94A3B8] hover:text-white transition">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <AnimatePresence>
          {showResults && search.trim() && (
            <motion.div
              initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
              className="absolute z-30 mt-2 w-full rounded-xl border border-white/10 bg-[#0B0F19] shadow-2xl overflow-hidden"
              data-testid="ai-human-search-results">
              {results.length === 0 ? (
                <div className="px-4 py-3 text-sm text-[#64748B]">No matches. Try "voice", "images" or a tool name.</div>
              ) : results.map((r, i) => {
                const Icon = ICONS[r.part.icon] || Sparkles;
                return (
                  <button key={`${r.partId}-${r.label}-${i}`} onClick={() => chooseResult(r)}
                    data-testid={`search-result-${i}`}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-white/[0.05] transition">
                    <span className="grid place-items-center w-8 h-8 rounded-lg shrink-0"
                      style={{ color: r.part.color, background: `${r.part.color}16` }}>
                      <Icon className="w-4 h-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold text-white truncate">{r.label}</span>
                      <span className="block text-[11px] text-[#94A3B8] truncate">{r.sub}</span>
                    </span>
                    <span className="text-[10px] uppercase tracking-wide text-[#64748B] shrink-0">{r.kind}</span>
                  </button>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Main interactive */}
      <div className="grid grid-cols-1 xl:grid-cols-[1.15fr_0.85fr] gap-6 mt-8 items-start">
        {/* Figure + labels */}
        <div className="card-surface rounded-3xl p-6 relative overflow-hidden" data-testid="ai-human-map">
          <div className="absolute inset-0 pointer-events-none opacity-[0.06]"
            style={{ backgroundImage: "radial-gradient(circle at 50% 40%, #00F0FF, transparent 60%)" }} />

          {/* Desktop: flanking labels */}
          <div className="hidden lg:grid grid-cols-[1fr_auto_1fr] items-center gap-2 relative z-10">
            <div className="flex flex-col gap-2 items-end">
              {leftParts.map((p) => (
                <PartLabel key={p.id} part={p} align="right" active={p.id === selected} onClick={() => pick(p.id)} />
              ))}
            </div>

            <div className="relative inline-block mx-auto">
              <img src={data.figure_image} alt="AI Human holographic figure" data-testid="ai-human-figure"
                className="h-[560px] w-auto object-contain select-none" draggable={false} />
              {parts.map((p) => (
                <Hotspot key={p.id} part={p} active={p.id === selected} onClick={() => pick(p.id)} />
              ))}
            </div>

            <div className="flex flex-col gap-2 items-start">
              {rightParts.map((p) => (
                <PartLabel key={p.id} part={p} align="left" active={p.id === selected} onClick={() => pick(p.id)} />
              ))}
            </div>
          </div>

          {/* Mobile: figure + chip grid */}
          <div className="lg:hidden relative z-10">
            <div className="relative inline-block mx-auto block w-fit">
              <img src={data.figure_image} alt="AI Human holographic figure"
                className="h-[360px] w-auto object-contain mx-auto select-none" draggable={false} />
              {parts.map((p) => (
                <Hotspot key={p.id} part={p} active={p.id === selected} onClick={() => pick(p.id)} />
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2 mt-5">
              {parts.map((p) => (
                <PartLabel key={p.id} part={p} align="left" testId={`part-mobile-${p.id}`} active={p.id === selected} onClick={() => pick(p.id)} />
              ))}
            </div>
          </div>
        </div>

        {/* Detail panel */}
        <AnimatePresence mode="wait">
          {active && (
            <motion.div
              key={active.id}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3 }}
              className="card-surface rounded-3xl p-6 xl:sticky xl:top-24"
              data-testid="part-detail"
            >
              <div className="flex items-start gap-4">
                <span className="grid place-items-center w-16 h-16 rounded-2xl shrink-0"
                  style={{ color: active.color, background: `${active.color}16`, boxShadow: `inset 0 0 0 1px ${active.color}44` }}>
                  {React.createElement(ICONS[active.icon] || Sparkles, { className: "w-8 h-8" })}
                </span>
                <div className="flex-1 min-w-0">
                  <h2 className="font-display font-extrabold text-3xl text-white leading-none" data-testid="detail-name">{active.name}</h2>
                  <p className="text-sm mt-1.5" style={{ color: active.color }}>{active.subtitle}</p>
                </div>
                <div ref={shareRef} className="relative shrink-0">
                  <button onClick={() => setShareOpen((o) => !o)} data-testid="share-part-btn" title="Share this ability"
                    className="grid place-items-center w-9 h-9 rounded-lg border border-white/10 text-[#94A3B8] hover:text-[#00F0FF] hover:border-[#00F0FF]/40 transition">
                    <Share2 className="w-4 h-4" />
                  </button>
                  <AnimatePresence>
                    {shareOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -6, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -6, scale: 0.96 }}
                        transition={{ duration: 0.15 }}
                        className="absolute right-0 mt-2 w-44 rounded-xl border border-white/10 bg-[#0B0F19] shadow-2xl overflow-hidden z-30"
                        data-testid="share-menu">
                        <button onClick={copyLink} data-testid="share-copy"
                          className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-[#CBD5E1] hover:bg-white/[0.05] transition">
                          {copied ? <Check className="w-4 h-4 text-[#22C55E]" /> : <Link2 className="w-4 h-4 text-[#94A3B8]" />}
                          {copied ? "Copied!" : "Copy link"}
                        </button>
                        <button onClick={() => shareOn("twitter")} data-testid="share-twitter"
                          className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-[#CBD5E1] hover:bg-white/[0.05] transition">
                          <Twitter className="w-4 h-4 text-[#1DA1F2]" /> Share on X
                        </button>
                        <button onClick={() => shareOn("linkedin")} data-testid="share-linkedin"
                          className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-[#CBD5E1] hover:bg-white/[0.05] transition">
                          <Linkedin className="w-4 h-4 text-[#0A66C2]" /> Share on LinkedIn
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
              <p className="text-[#94A3B8] text-sm mt-4 leading-relaxed">{active.description}</p>

              <div className="flex items-center justify-between mt-6 mb-3">
                <h3 className="font-display font-bold" style={{ color: active.color }}>Top AI Tools</h3>
                <span className="text-xs text-[#64748B]">{active.tools.length} tools</span>
              </div>

              <div className="space-y-3 max-h-[560px] overflow-y-auto pr-1 -mr-1">
                {active.tools.map((t, i) => (
                  <ToolRow key={`${active.id}-${i}`} tool={t} index={i} color={active.color} />
                ))}
              </div>

              <div className="mt-5 flex items-center gap-2 text-[11px] text-[#64748B] leading-snug">
                <Info className="w-3.5 h-3.5 shrink-0" />
                <span>Usage figures are from public reports (Similarweb, Business of Apps, Stack Overflow). Tools without a verified public figure show market position instead.</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* What is AI Human */}
      <div className="card-surface rounded-3xl p-6 md:p-8 mt-8 flex flex-col lg:flex-row lg:items-center gap-8" data-testid="ai-human-about">
        <div className="lg:max-w-md">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center w-12 h-12 rounded-xl text-[#00F0FF] bg-[#00F0FF]/12">
              <Sparkles className="w-6 h-6" />
            </span>
            <h3 className="font-display font-bold text-xl">What is AI Human?</h3>
          </div>
          <p className="text-[#94A3B8] text-sm mt-3 leading-relaxed">
            AI Human maps human body parts to the AI tools that enhance those abilities, solve real problems,
            or boost creativity — ranked using verified, publicly-sourced adoption data.
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 flex-1">
          {WHATS.map((w) => (
            <div key={w.title} className="text-center">
              <span className="grid place-items-center w-11 h-11 rounded-xl text-[#00F0FF] bg-white/[0.04] mx-auto">
                <w.icon className="w-5 h-5" />
              </span>
              <div className="font-display font-bold text-white mt-2 text-sm">{w.title}</div>
              <div className="text-xs text-[#94A3B8] mt-0.5">{w.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Popular body parts */}
      <div className="mt-8">
        <div className="flex items-end justify-between mb-4">
          <div>
            <h3 className="font-display font-bold text-2xl">Popular Body Parts</h3>
            <p className="text-[#94A3B8] text-sm mt-1">Explore the most popular human abilities and their AI tools</p>
          </div>
          <div className="hidden md:flex gap-2">
            <button data-testid="carousel-prev" onClick={() => carouselRef.current?.scrollBy({ left: -300, behavior: "smooth" })}
              className="grid place-items-center w-9 h-9 rounded-lg border border-white/10 text-[#94A3B8] hover:text-[#00F0FF] hover:border-[#00F0FF]/40 transition">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button data-testid="carousel-next" onClick={() => carouselRef.current?.scrollBy({ left: 300, behavior: "smooth" })}
              className="grid place-items-center w-9 h-9 rounded-lg border border-white/10 text-[#94A3B8] hover:text-[#00F0FF] hover:border-[#00F0FF]/40 transition">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div ref={carouselRef} className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide" style={{ scrollbarWidth: "none" }}>
          {popularOrdered.map((id) => {
            const p = byId[id];
            if (!p) return null;
            const Icon = ICONS[p.icon] || Sparkles;
            const on = id === selected;
            const v = views[id] || 0;
            return (
              <button key={id} data-testid={`popular-${id}`} onClick={() => pick(id)}
                className="shrink-0 w-32 rounded-2xl p-4 flex flex-col items-center gap-2 transition-all card-surface hover:-translate-y-1"
                style={on ? { boxShadow: `inset 0 0 0 1px ${p.color}66`, background: `${p.color}12` } : {}}>
                <span className="grid place-items-center w-12 h-12 rounded-xl"
                  style={{ color: p.color, background: `${p.color}16` }}>
                  <Icon className="w-6 h-6" />
                </span>
                <span className="font-display font-bold text-sm text-white">{p.name}</span>
                <span className="text-[10px] text-[#94A3B8] flex items-center gap-1" data-testid={`popular-views-${id}`}>
                  {v > 0 ? `${v.toLocaleString()} views` : `${p.tools.length} tools`} <ArrowRight className="w-3 h-3" />
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
